// "Report details & the cover sheet" — the persisted project/report identity,
// the current standards controls, exactly where those values print, and the
// present limits of the deflection-default and building-code fields.
import type { HelpArticle } from "../../types";
import { MAPLE_PROJECT } from "../../../calculations/calcPreviewFixtures";
import { CALC_EXPORT_COPY } from "../../../../api/calcExportCopy";

export const reportDetailsArticle: HelpArticle = {
  id: "report-details",
  title: "Report details & the cover sheet",
  description:
    "Set the project identity and report basis, understand the design-method save, and see exactly what the project cover prints.",
  tags: ["report details", "cover", "engineer", "job number", "code basis", "design method"],
  lastUpdated: "July 20, 2026",
  published: false,
  blocks: [
    {
      kind: "lead",
      text: "Report details is the project record behind a calculation package. It holds the title, job and client identity, responsible engineer, code-basis labels, and project design method. The project cover reads that saved record when an export starts; it does not ask for a second copy in the PDF wizard.",
    },

    { kind: "heading", level: 2, text: "The saved project identity", id: "identity" },
    { kind: "where", path: ["Calculations", MAPLE_PROJECT.name, "Report details"] },
    {
      kind: "paragraph",
      text: "The first two bands write into two kinds of project data. **Report title** and **Address** are the project's shared name and address, so changing either also changes the identity shown by the calculation shell. Job number, client, engineer, license, building code, risk category, and the two deflection entries have dedicated report fields. An empty optional report field is saved as unset.",
    },
    {
      kind: "values",
      caption: "The Maple St. fixture used by the form and cover figures.",
      rows: [
        { label: "Report title", value: MAPLE_PROJECT.name },
        { label: "Address", value: MAPLE_PROJECT.address },
        { label: "Job number", value: MAPLE_PROJECT.jobNumber },
        { label: "Client", value: MAPLE_PROJECT.client },
        { label: "Engineer / license", value: `${MAPLE_PROJECT.engineer} / ${MAPLE_PROJECT.license}` },
        { label: "Code basis / risk", value: `${MAPLE_PROJECT.buildingCode} / ${MAPLE_PROJECT.riskCategory}` },
      ],
    },
    {
      kind: "shot",
      shotId: "report-details-form",
      caption: "One project record: report identity, engineer, and the current standards controls on the same page.",
    },
    {
      kind: "paragraph",
      text: "The form does not auto-save. **Save report details** stays disabled until the local form differs from the last saved response. A successful save reloads the form from the API response and shows **Saved.**; the message disappears on the next edit. A project refresh may replace an untouched form, but it cannot overwrite edits already in progress.",
    },
    {
      kind: "callout",
      variant: "note",
      title: "The cover is a snapshot",
      body: "Generating an export freezes the report identity with the selected calculations. Editing Report details afterward changes the next package, not the PDF job already created.",
    },

    { kind: "heading", level: 2, text: "What the cover prints", id: "cover" },
    {
      kind: "paragraph",
      text: "A project export can place a cover before the calculation index and sheet pages. Its title block prints the project name and address. The details grid always carries six fields: job number, client, engineer, license, code basis, and risk category. In the current renderer, an unset value prints as a dash; it is not omitted. The summary counts only the calculations frozen into that export, including how many have `passes === false` and how many retain `draft` status.",
    },
    {
      kind: "shot",
      shotId: "report-details-cover",
      caption: "The project cover generated from the saved report identity, followed by selected-set counts and the engineering-review notice.",
    },
    {
      kind: "paragraph",
      text: `Every calculation page has its own header and footer even when the project cover is off. The header prints the calculation mark, calculator title, that calculator's registry standard, and the job number (falling back to the project name when no job number exists). The primary page footer prints the project and mark, generation date, and either **${CALC_EXPORT_COPY.forEngineeringReview}** or **${CALC_EXPORT_COPY.reviewedCalculation}** from the sheet's stored review flag. A detailed non-load appendix repeats that flag-derived label. A detailed load emits a continuation only when its input arrays exceed the renderer's split threshold; that continuation always prints **${CALC_EXPORT_COPY.forEngineeringReview}**.`,
    },
    {
      kind: "callout",
      variant: "warning",
      title: "A cover is not a seal",
      body: `The cover states: "${CALC_EXPORT_COPY.coverDisclaimer}" Report identity and a saved license number do not apply a professional seal.`,
    },

    { kind: "heading", level: 2, text: "Code basis and design method", id: "design-method" },
    {
      kind: "paragraph",
      text: "Two entries are fixed on this screen: **AWC NDS 2024** and **ASCE 7-22**. The NDS label describes the current wood-member basis shown by Report details; it is not a project-wide standard for every possible sheet. Steel, concrete, masonry, connection, analysis, and load pages print the standard assigned to their calculator in the export registry.",
    },
    {
      kind: "shot",
      shotId: "report-details-basis",
      caption: "The current basis controls: two fixed labels, the project design method, report metadata, and the present deflection fields.",
    },
    {
      kind: "paragraph",
      text: "**Design method** is active project behavior. Saving a change to ASD or LRFD updates the project default and recomputes non-load sheets in load-path order. A sheet with an explicit member override keeps that override, but it is still recomputed because an upstream reaction may have changed. Nominal load-calculation sheets are preserved rather than rerun; their actions remain unfactored inputs to downstream combinations.",
    },
    {
      kind: "callout",
      variant: "warning",
      title: "Two current boundaries",
      body: `The Building code menu currently offers IBC 2021, IBC 2018, and IRC 2021. A stored value outside that set, including the fixture's ${MAPLE_PROJECT.buildingCode}, appears blank in the closed select. The LL/TL deflection entries are persisted and copied into an export snapshot, but no calculation consumes them as defaults today. Set deflection criteria on the sheet itself; treat these two project fields as report metadata until propagation is implemented.`,
    },
    {
      kind: "paragraph",
      text: "A design-method save is also the only multi-request operation on this page. The method change and graph recomputation complete first; the remaining report fields are patched afterward. If the second request fails, the form reports the save error even though the method may already have changed. Reopen the page and verify the project method before retrying the identity fields.",
    },

    { kind: "related", topics: ["calc-export", "your-first-member", "design-method"] },
  ],
};
